import type { DocumentMetadata } from './document-metadata.model';
import type { SplitDocumentGroup } from './document-workbench.model';

export interface DocumentReviewDraft {
  analysisComplete: boolean;
  metadata: DocumentMetadata;
  groups: SplitDocumentGroup[];
  multiDocument: boolean;
  keptAsSingle: boolean;
  reviewedIds: string[];
  qualityAcknowledged: boolean;
}
export interface DocumentReviewNavigation {
  previous: boolean;
  next: boolean;
  draft?: DocumentReviewDraft;
  navigate: (action: 'previous' | 'next' | 'skip' | 'finish', draft: DocumentReviewDraft) => void;
}

/** Keep skipped files in the batch; only successful uploads leave the pending queue. */
export async function reviewDocumentQueue(
  files: File[],
  review: (file: File, queue: { position: number; total: number; navigation?: DocumentReviewNavigation }) => Promise<boolean>
): Promise<{ uploaded: number; skipped: number }> {
  const pending = files.map((file) => ({ file, draft: undefined as DocumentReviewDraft | undefined }));
  let index = 0;
  let uploaded = 0;
  while (pending.length) {
    const entry = pending[index];
    let action: 'previous' | 'next' | 'skip' | 'finish' | undefined;
    const navigable = files.length > 1 && !entry.file.name.toLowerCase().endsWith('.zip');
    const success = await review(entry.file, {
      position: index + 1,
      total: pending.length,
      navigation: navigable ? {
        previous: index > 0,
        next: index < pending.length - 1,
        draft: entry.draft,
        navigate: (next, draft) => { action = next; entry.draft = draft; },
      } : undefined,
    });
    if (success) {
      uploaded++;
      pending.splice(index, 1);
      index = Math.min(index, pending.length - 1);
    } else if (action === 'finish') {
      break;
    } else if (action === 'previous') {
      index = Math.max(0, index - 1);
    } else if (action === 'next' || action === 'skip') {
      index = (index + 1) % pending.length;
    } else {
      // Single-file and ZIP dialogs retain their existing cancellation behavior.
      index++;
      if (index >= pending.length) break;
    }
  }
  return { uploaded, skipped: files.length - uploaded };
}
